import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AuthenticatedProviders } from './components/AuthenticatedProviders'
import { GuestOnly } from './components/GuestOnly'
import { RequireAuth } from './components/RequireAuth'
import { RequireRoutePermission } from './components/RequireRoutePermission'
import { ThemedPermissionLoadingState } from './components/theme/ThemedAccessDeniedState'
import { useUserAuthorization } from './context/UserAuthorizationContext'
import { AppLayout } from './layouts/AppLayout'
import { getFirstAccessiblePath } from './lib/userNavAccess'
import { ComicEditorPage } from './pages/ComicEditorPage'
import { ComicProjectsPage } from './pages/ComicProjectsPage'
import { CreatePage } from './pages/CreatePage'
import { ForbiddenPage } from './pages/ForbiddenPage'
import { AgentsPage } from './pages/AgentsPage'
import { LibraryPage } from './pages/LibraryPage'
import { LoginPage } from './pages/LoginPage'
import { ProfilePage } from './pages/ProfilePage'
import { WorkflowsPage } from './pages/WorkflowsPage'
import { useTheme } from './theme/ThemeContext'

const WorkflowBuilderPage = lazy(() =>
  import('./pages/WorkflowBuilderPage').then((module) => ({ default: module.WorkflowBuilderPage })),
)
const WorkflowRunPage = lazy(() =>
  import('./pages/WorkflowRunPage').then((module) => ({ default: module.WorkflowRunPage })),
)

function IndexRedirect() {
  const { theme } = useTheme()
  const { permissions, loaded } = useUserAuthorization()
  if (!loaded) {
    return <ThemedPermissionLoadingState theme={theme} className="flex-1" />
  }
  return <Navigate to={getFirstAccessiblePath(permissions)} replace />
}

function RouteLoadingFallback() {
  const { theme } = useTheme()
  return <ThemedPermissionLoadingState theme={theme} className="flex-1" />
}

export default function App() {
  return (
    <Routes>
      <Route element={<GuestOnly />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      <Route element={<RequireAuth />}>
        <Route element={<AuthenticatedProviders />}>
          <Route element={<AppLayout />}>
            <Route index element={<IndexRedirect />} />
            <Route path="forbidden" element={<ForbiddenPage />} />
            <Route
              path="library"
              element={
                <RequireRoutePermission>
                  <LibraryPage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="comics"
              element={
                <RequireRoutePermission>
                  <ComicProjectsPage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="comics/:projectId"
              element={
                <RequireRoutePermission>
                  <ComicEditorPage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="create"
              element={
                <RequireRoutePermission>
                  <CreatePage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="agents"
              element={
                <RequireRoutePermission>
                  <AgentsPage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="workflows"
              element={
                <RequireRoutePermission>
                  <WorkflowsPage />
                </RequireRoutePermission>
              }
            />
            <Route
              path="workflows/builder/:definitionId/simulate"
              element={
                <RequireRoutePermission>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <WorkflowRunPage />
                  </Suspense>
                </RequireRoutePermission>
              }
            />
            <Route
              path="workflows/builder/:definitionId"
              element={
                <RequireRoutePermission>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <WorkflowBuilderPage />
                  </Suspense>
                </RequireRoutePermission>
              }
            />
            <Route
              path="workflows/runs/:runId"
              element={
                <RequireRoutePermission>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <WorkflowRunPage />
                  </Suspense>
                </RequireRoutePermission>
              }
            />
            <Route path="plans" element={<Navigate to="/profile" replace />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  )
}
